import {
  BookOpen,
  Briefcase,
  Building2,
  ClipboardList,
  Folder,
  GraduationCap,
  House,
  Map as MapIcon,
  Medal,
  Newspaper,
  Shapes,
  Smartphone,
  TrendingUp,
  UserCog,
  Users,
  UsersRound,
} from 'lucide-react';
import { CustomRoutes, Resource, ResourceContextProvider } from 'ra-core';
import { useMemo } from 'react';
import { Outlet, Route } from 'react-router-dom';
import { useCanAccessUsersResource } from '../auth/useCanAccessUsersResource';
import { useHasPermission } from '../auth/useHasPermission';
import { useIsUnitManager } from '../auth/useIsUnitManager';
import { ResourcesValue, CustomResourcesValue } from './resources';
import { BadgesAnalytics } from './Badges/BadgesAnalytics';
import { BadgesCreate } from './Badges/BadgesCreate';
import { BadgesEdit } from './Badges/BadgesEdit';
import { BadgesList } from './Badges/BadgesList';
import { SingleBadgeAnalytics } from './Badges/SingleBadgeAnalytics';
import { DepartmentCreate } from './Department/DepartmentCreate';
import { DepartmentEdit } from './Department/DepartmentEdit';
import { DepartmentList } from './Department/DepartmentList';
import { GroupsCustom } from './Chat/GroupsCustom';
import { JobTitleCreate } from './JobTitle/JobTitleCreate';
import { JobTitleEdit } from './JobTitle/JobTitleEdit';
import { JobTitleList } from './JobTitle/JobTitleList';
import { NudgeCategoryCreate } from './NudgeCategory/NudgeCategoryCreate';
import { NudgeCategoryEdit } from './NudgeCategory/NudgeCategoryEdit';
import { NudgeCategoryList } from './NudgeCategory/NudgeCategoryList';
import { NudgeCreate } from './Nudges/NudgeCreate';
import { NudgeEdit } from './Nudges/NudgeEdit';
import { NudgeFeedbackList } from './Nudges/NudgeFeedback/NudgeFeedbackList';
import { NudgeList } from './Nudges/NudgeList';
import { NudgeSlideCreate } from './Nudges/NudgeSlide/NudgeSlideCreate';
import { NudgeSlideEdit } from './Nudges/NudgeSlide/NudgeSlideEdit';
import { NudgeSlideList } from './Nudges/NudgeSlide/NudgeSlideList';
import { PostList } from './Posts/PostList';
import { RegionCreate } from './Region/RegionCreate';
import { RegionEdit } from './Region/RegionEdit';
import { RegionList } from './Region/RegionList';
import { SmsMessageList } from './SmsMessages/SmsMessageList';
import { TopicCreate } from './Topic/TopicCreate';
import { TopicEdit } from './Topic/TopicEdit';
import { TopicList } from './Topic/TopicList';
import { UnitCreate } from './Unit/UnitCreate';
import { UnitEdit } from './Unit/UnitEdit';
import { UnitList } from './Unit/UnitList';
import { UserEdit } from './User/UserEdit';
import { UserCreate } from './User/UserCreate';
import { UserList } from './User/UserList';
import { UserGroupList } from './UserGroup/UserGroupList';
import { UserGroupCreate } from './UserGroup/UserGroupCreate';
import { UserGroupEdit } from './UserGroup/UserGroupEdit';
import { UserGroupMembershipList } from './UserGroup/UserGroupMembership/UserGroupMembershipList';
import { UsersAnalytics } from './Dashboard/Users/Analytics';
import { PulseChecksChart } from './Dashboard/PulseChecks/Chart';
import { FeedAnalytics } from './Dashboard/Feed/Analytics';
import { RoleUsersList } from './Role/Users/RoleUsersList';
import { RoleList } from './Role/RoleList';
import { RoleCreate } from './Role/RoleCreate';
import { RoleEdit } from './Role/RoleEdit';
import { KpiList } from './Kpis/KpiList';
import { KpiCreate } from './Kpis/KpiCreate';
import { KpiEdit } from './Kpis/KpiEdit';
import { KpiAssigneeList } from './Kpis/KpiAssignee/KpiAssigneeList';
import { KpiDataList } from './Kpis/KpiData/KpiDataList';
import { JourneysCreate } from './Journeys/JourneysCreate';
import { JourneysList } from './Journeys/JourneysList';
import { JourneysEdit } from './Journeys/JourneysEdit';
import { UnitPerformancePage } from './Unit/Performance/UnitPerformancePage';
import { UnitInsightsPage } from './Unit/Performance/UnitInsightsPage';
import { JourneyAnalytics } from './Journeys/JourneyAnalytics';
import { ActivityAnalytics } from './Journeys/ActivityAnalytics';
import { ChecklistsCreatePage } from './Checklists/ChecklistsCreate';
import { ChecklistDetailsPage } from './Checklists/ChecklistDetailsPage';
import { ChecklistOverviewRoute } from './Checklists/ChecklistOverviewRoute';
import { ChecklistsListPage } from './Checklists/ChecklistsList';
import { UnitInsightsLlmChatPage } from './Unit/Performance/Chat/UnitInsightsLlmChatPage';
import { ChecklistsEditPage } from './Checklists/ChecklistsEdit';
import { PromptTest } from './Unit/Performance/Chat/PromptTest';

export type AdminResourcePermissions = {
  isAdmin: boolean;
  hasChatAccess: boolean;
  canAccessUsers: boolean;
  hasNudgesAccess: boolean;
  hasPerformIQAccess: boolean;
  hasJourneysAccess: boolean;
  hasProceduresAccess: boolean;
  hasPoliciesAccess: boolean;
  hasChecklistAdminAccess: boolean;
  isUnitManager: boolean;
};

export const useAdminResourcePermissions = (): AdminResourcePermissions => {
  const isAdmin = useHasPermission('ADMIN_ACCESS');
  const hasChatAccess = useHasPermission('CHAT_ACCESS');
  const canAccessUsers = useCanAccessUsersResource();
  const hasNudgesAccess = useHasPermission('NUDGES_ACCESS');
  const hasPerformIQAccess = useHasPermission('PERFORM_IQ_KPIS_ACCESS');
  const hasJourneysAccess = useHasPermission('JOURNEYS_ACCESS');
  const hasProceduresAccess = useHasPermission('PROCEDURES_ACCESS');
  const hasPoliciesAccess = useHasPermission('POLICIES_ACCESS');
  const hasChecklistAdminAccess = useHasPermission('CHECKLIST_ADMIN');
  const isUnitManager = useIsUnitManager();

  return useMemo(
    () => ({
      isAdmin,
      hasChatAccess,
      canAccessUsers,
      hasNudgesAccess,
      hasPerformIQAccess,
      hasJourneysAccess,
      hasProceduresAccess,
      hasPoliciesAccess,
      hasChecklistAdminAccess,
      isUnitManager,
    }),
    [
      isAdmin,
      hasChatAccess,
      canAccessUsers,
      hasNudgesAccess,
      hasPerformIQAccess,
      hasJourneysAccess,
      hasProceduresAccess,
      hasPoliciesAccess,
      hasChecklistAdminAccess,
      isUnitManager,
    ]
  );
};

/**
 * Returns the `<Resource>` and `<CustomRoutes>` elements. Called as a function (not rendered as a component) because
 * ra-core reads the resources from the direct children of `<Admin>`.
 */
export const AdminResources = ({
  isAdmin,
  hasChatAccess,
  canAccessUsers,
  hasNudgesAccess,
  hasPerformIQAccess,
  hasJourneysAccess,
  hasProceduresAccess,
  hasPoliciesAccess,
  hasChecklistAdminAccess,
  isUnitManager,
}: AdminResourcePermissions) => {
  return (
    <>
      <Resource name={ResourcesValue.POSTS} icon={Newspaper} list={PostList} />
      <CustomRoutes>
        {isAdmin && (
          <>
            <Route
              path={`/${CustomResourcesValue.DASHBOARD_USERS}`}
              element={<UsersAnalytics />}
            />
            <Route
              path={`/${CustomResourcesValue.DASHBOARD_FEED}`}
              element={<FeedAnalytics />}
            />
            <Route
              path={`/${CustomResourcesValue.DASHBOARD_PULSE_CHECKS}`}
              element={<PulseChecksChart />}
            />
          </>
        )}
        {hasChatAccess && (
          <Route
            path={`/${CustomResourcesValue.CHAT}`}
            element={<GroupsCustom />}
          />
        )}
      </CustomRoutes>
      {canAccessUsers && (
        <Resource
          name={ResourcesValue.USERS}
          icon={Users}
          list={UserList}
          create={UserCreate}
          edit={UserEdit}
        />
      )}
      {isAdmin ? (
        <>
          <Resource
            name={ResourcesValue.ROLES}
            icon={UserCog}
            list={RoleList}
            create={RoleCreate}
            edit={RoleEdit}
          >
            {canAccessUsers && (
              <>
                <Route
                  path=":roleId/users"
                  element={
                    <ResourceContextProvider value={ResourcesValue.ROLE_USERS}>
                      <RoleUsersList displaying="users" />
                    </ResourceContextProvider>
                  }
                />
                <Route
                  path=":roleId/users/add"
                  element={
                    <ResourceContextProvider value={ResourcesValue.ROLE_USERS}>
                      <RoleUsersList displaying="non-users" />
                    </ResourceContextProvider>
                  }
                />
              </>
            )}
          </Resource>
          <Resource
            name={ResourcesValue.SMS}
            options={{ label: 'Welcome SMS Status' }}
            icon={Smartphone}
            list={SmsMessageList}
          />
          <Resource
            name={ResourcesValue.DEPARTMENTS}
            icon={Building2}
            list={DepartmentList}
            edit={DepartmentEdit}
            create={DepartmentCreate}
          />
          <Resource
            name={ResourcesValue.JOB_TITLES}
            icon={Briefcase}
            list={JobTitleList}
            edit={JobTitleEdit}
            create={JobTitleCreate}
          />
          <Resource
            name={ResourcesValue.REGIONS}
            icon={MapIcon}
            list={RegionList}
            edit={RegionEdit}
            create={RegionCreate}
          />
          <Resource
            name={ResourcesValue.UNITS}
            icon={House}
            list={UnitList}
            edit={UnitEdit}
            create={UnitCreate}
          >
            {hasPerformIQAccess && (
              <>
                <Route
                  path=":unitId/performance"
                  element={<UnitPerformancePage />}
                />
                <Route
                  path=":unitId/performance/insights"
                  element={<UnitInsightsPage />}
                />
                <Route
                  path=":unitId/performance/insights/:insightId/chat"
                  element={<UnitInsightsLlmChatPage />}
                />
                <Route path="performiq/test-chat" element={<PromptTest />} />
              </>
            )}
          </Resource>
          {hasPerformIQAccess ? (
            <Resource
              name={ResourcesValue.KPIS}
              icon={TrendingUp}
              list={KpiList}
              create={KpiCreate}
              edit={KpiEdit}
            >
              <Route
                path=":kpiId/assignees/add"
                element={
                  <ResourceContextProvider value={ResourcesValue.KPI_ASSIGNEES}>
                    <KpiAssigneeList />
                  </ResourceContextProvider>
                }
              />
              <Route
                path=":kpiId/data"
                element={
                  <ResourceContextProvider value={ResourcesValue.KPI_DATA}>
                    <KpiDataList />
                  </ResourceContextProvider>
                }
              />
            </Resource>
          ) : undefined}
          <Resource
            name={ResourcesValue.TOPICS}
            icon={Folder}
            list={TopicList}
            edit={TopicEdit}
            create={TopicCreate}
          />
          {hasNudgesAccess ? (
            <>
              <Resource
                name={ResourcesValue.NUDGES}
                icon={GraduationCap}
                list={NudgeList}
                create={NudgeCreate}
                edit={NudgeEdit}
              >
                <Route
                  path=":nudgeId/slides"
                  element={
                    <ResourceContextProvider value="nudgeSlides">
                      <Outlet />
                    </ResourceContextProvider>
                  }
                >
                  <Route path="" element={<NudgeSlideList />} />
                  <Route path="create" element={<NudgeSlideCreate />} />
                  <Route path=":id" element={<NudgeSlideEdit />} />
                </Route>
                <Route
                  path=":nudgeId/feedbacks"
                  element={
                    <ResourceContextProvider value="nudgeFeedbacks">
                      <NudgeFeedbackList />
                    </ResourceContextProvider>
                  }
                />
              </Resource>
              <Resource
                name={ResourcesValue.NUDGE_CATEGORY}
                icon={Shapes}
                list={NudgeCategoryList}
                create={NudgeCategoryCreate}
                edit={NudgeCategoryEdit}
              />
            </>
          ) : undefined}
          <Resource
            name={ResourcesValue.USER_GROUPS}
            options={{ label: 'User Groups' }}
            icon={UsersRound}
            list={UserGroupList}
            create={UserGroupCreate}
            edit={UserGroupEdit}
          >
            {canAccessUsers && (
              <>
                <Route
                  path=":groupId/members"
                  element={
                    <ResourceContextProvider
                      value={ResourcesValue.USER_GROUP_MEMBERS}
                    >
                      <UserGroupMembershipList displaying="members" />
                    </ResourceContextProvider>
                  }
                />
                <Route
                  path=":groupId/members/add"
                  element={
                    <ResourceContextProvider
                      value={ResourcesValue.USER_GROUP_MEMBERS}
                    >
                      <UserGroupMembershipList displaying="non-members" />
                    </ResourceContextProvider>
                  }
                />
              </>
            )}
          </Resource>
          {hasJourneysAccess || hasProceduresAccess || hasPoliciesAccess ? (
            <Resource
              name={ResourcesValue.JOURNEYS}
              options={{
                label: hasProceduresAccess
                  ? 'Procedures'
                  : hasPoliciesAccess
                    ? 'Policies'
                    : 'Journeys',
              }}
              icon={BookOpen}
              list={JourneysList}
              edit={JourneysEdit}
              create={JourneysCreate}
            >
              <Route path="/:id/analytics" element={<JourneyAnalytics />} />
              <Route
                path="/:journeyId/sections/:sectionId/activities/:activityId/analytics"
                element={<ActivityAnalytics />}
              />
            </Resource>
          ) : null}
          <Resource
            name={ResourcesValue.BADGES}
            icon={Medal}
            list={BadgesList}
            edit={BadgesEdit}
            create={BadgesCreate}
          />
          <Resource
            name={ResourcesValue.BADGES_AWARDS}
            options={{ label: 'Awarded Badges' }}
          >
            <Route path="/analytics" element={<BadgesAnalytics />} />
          </Resource>
          <Resource
            name={ResourcesValue.BADGE_AWARDS}
            options={{ label: 'Awarded Badges' }}
          >
            <Route path="/analytics/:id" element={<SingleBadgeAnalytics />} />
          </Resource>
        </>
      ) : undefined}
      {hasChecklistAdminAccess && (
        <Resource
          name={ResourcesValue.CHECKLISTS}
          icon={ClipboardList}
          list={ChecklistsListPage}
          create={isUnitManager ? undefined : ChecklistsCreatePage}
          edit={isUnitManager ? undefined : ChecklistsEditPage}
        >
          <Route path=":id" element={<ChecklistOverviewRoute />} />
          <Route path=":id/details" element={<ChecklistDetailsPage />} />
        </Resource>
      )}
    </>
  );
};
